import { ICourse } from '@/types/courses';
import Text from '@ui/text/text/text';
import { useCallback, useEffect, useRef, useState } from 'react';
import NumericStepper from '@ui/numeric-stepper/numeric-stepper';
import { numClamp } from '@/utils/num-clamp';
import Container from '@ui/container/container';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import CoursesCategoriesSelect from '../controls/courses-categories-select';
import { defaultCategories } from '../utils/course-requirement-defaults';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import TextArea from '@ui/text-area/text-area';
import CourseBadge from '../icons/course-badge';

type CourseDetailProps = {
  handleSave: (name?: string, description?: string, categories?: string[], credits?: number) => void;
  course: ICourse | null;
};

function CourseDetail({ handleSave, course }: CourseDetailProps) {
  const [courseDescription, setCourseDescription] = useState(course?.description);
  const [newCourseName, setNewCourseName] = useState(course?.name.base || '');
  const descriptionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setCourseDescription(course?.description);
  }, [course?.description]);

  useEffect(() => {
    setNewCourseName(course?.name.base || '');
  }, [course?.name.base]);

  const handleAddCategories = (selectedCategories: TReactSelectOption[]) => {
    const updatedCategories = selectedCategories.map((c) => c.value.toString());
    handleSave('', '', updatedCategories);
  };

  const debouncedSaveDescription = useCallback(
    (value: string) => {
      if (descriptionTimeoutRef.current) {
        clearTimeout(descriptionTimeoutRef.current);
      }

      descriptionTimeoutRef.current = setTimeout(() => {
        handleSave('', value);
      }, 500);
    },
    [handleSave]
  );

  useEffect(() => {
    return () => {
      if (descriptionTimeoutRef.current) {
        clearTimeout(descriptionTimeoutRef.current);
      }
    };
  }, []);

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setCourseDescription(value);
      debouncedSaveDescription(value);
    },
    [debouncedSaveDescription]
  );

  const handleDescriptionBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      if (descriptionTimeoutRef.current) {
        clearTimeout(descriptionTimeoutRef.current);
      }
      handleSave('', e.target.value);
    },
    [handleSave]
  );

  return (
    <>
      <Container className="flex-column">
        <Container className="flex ai-center jc-center header gap-10">
          <Container className="position-relative min-width-32">
            <CourseBadge height={32} width={32} />
            <Icon
              className="position-absolute top-10 left-10"
              name={ICONS.ACADEMIC_CAP}
              size={16}
              color="var(--gray-1200)"
            />
          </Container>
          <InputWithOverlay
            wrapperClass="flex-1 margin-t--8"
            design={null}
            border="1px solid transparent"
            hoverBorder="solid 1px var(--global-ico-text-3)"
            typography="H4"
            inputHeight="32px"
            inputProps={{
              value: newCourseName || '',
              placeholder: 'Give your Course a name',
              className: 'radius-4 padding-0',
              onChange: (e) => setNewCourseName(e.target.value),
              onBlur: (e) => handleSave(e.target.value),
            }}
          />
        </Container>
        <Container className="flex jc-space-between margin-t-24">
          <Container className="min-width-540 flex-column gap-4 flex-1" style={{ maxWidth: '480px' }}>
            <Text className="L2 margin-l-16" color="var(--global-ico-text-3)">
              Meta description
            </Text>
            <TextArea
              placeholder="Add a description to give your audience more context."
              value={courseDescription || ''}
              onChange={handleDescriptionChange}
              onBlur={handleDescriptionBlur}
            />
          </Container>
          <Container className="flex-column gap-10">
            <Text className="L2" color="var(--global-ico-text-3)">
              Credits
            </Text>
            <NumericStepper
              initialValue={course?.credits}
              minValue={0}
              maxValue={10000}
              step={1}
              onChange={(e) => {
                const credits = numClamp(e, 0, 10000);
                handleSave('', '', [], credits);
              }}
            />
          </Container>
          <Container className="flex-column gap-10">
            <Text className="L2" color="var(--global-ico-text-3)">
              Categories
            </Text>
            <Container width="164px">
              <CoursesCategoriesSelect
                defaultCategories={defaultCategories(course)}
                onMenuClose={handleAddCategories}
              />
            </Container>
          </Container>
        </Container>
      </Container>
    </>
  );
}

export default CourseDetail;
